import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Activity } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function NursingDashboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: p }, { data: a }] = await Promise.all([
          api.get("/patients", { params: { status: "admitted" } }),
          api.get("/dashboard/alerts"),
        ]);
        setRows(p); setAlerts(a);
      } catch (e) { console.warn("Nursing dashboard load failed", e); }
    };
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-6" data-testid="nursing-dashboard">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Nursing station</div>
        <h1 className="font-display text-3xl font-semibold mt-1">My patients</h1>
        <p className="text-sm text-muted-foreground mt-1">Assigned NICU patients — {rows.length} admitted · {alerts.length} alerts</p>
      </div>

      {alerts.length > 0 && (
        <div className="card-clinical p-4 border-l-4 border-l-red-500">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium flex items-center gap-2"><AlertTriangle size={14} className="status-critical" /> Active alerts</div>
            <span className="text-xs text-muted-foreground">{alerts.length}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {alerts.slice(0, 8).map((a, i) => (
              <div key={`${a.kind}:${a.patient_id || a.order_id || i}:${a.text}`} className={`text-xs rounded-full px-3 py-1 ${a.kind === "critical" ? "bg-critical" : "bg-warn"}`}>
                {a.text}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((p) => (
          <Link key={p.id} to={`/patients/${p.id}`} className="card-clinical p-5 hover:border-primary transition-colors">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{p.hospital_id} · Bed {p.bed_number || "—"}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                {p.critical && <Badge variant="destructive" className="rounded-full text-[10px]">CRITICAL</Badge>}
                {p.respiratory_support && p.respiratory_support !== "none" && <Badge className="rounded-full text-[10px] uppercase">{p.respiratory_support}</Badge>}
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.diagnosis || "—"}</div>
            <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
              <span><Activity size={12} className="inline mr-1" /> DOL {p.day_of_life ?? "—"}</span>
              <span>Wt {p.current_weight_g || p.birth_weight_g || "—"} g</span>
              <span>GA {p.gestational_age_weeks || "—"}w</span>
            </div>
          </Link>
        ))}
        {rows.length === 0 && <div className="col-span-full text-sm text-muted-foreground text-center py-12">No admitted patients.</div>}
      </div>
    </div>
  );
}
